import React, { useState, useEffect } from "react";
import { Loader2, AlertCircle, Search, Settings, Edit, ChartNoAxesColumnDecreasing } from "lucide-react";
import { fetchQuizzesByQuotaId } from "../../api/quizeApi";
import UpdateQuize from "./models/UpdateQuiz";
import QuizeQuestions from "./QuizeQuestions";
import LeaderBoard from "./models/LeaderBoard";
import AvailableQuizessSkeliton from "./components/AvailableQuizzesSkeliton";
const apiBase = process.env.REACT_APP_API;

const QuizList = ({ quotaId, refreshFlag , setRefreshFlag}) => {
  const [quizzes, setQuizzes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [pagination, setPagination] = useState({
    total: 0,
    page: 1,
    limit: 10,
    totalPages: 0,
  });

  const [selectedQuizId, setSelectedQuizId] = useState(null);
  const [selectedQuizData, setSelectedQuizData] = useState(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isQuestionsModalOpen, setIsQuestionsModalOpen] = useState(false);
  const [isLeaderBoardModelOpen, setIsLeaderBoardModelOpen] = useState(false);

  useEffect(() => {
    if (!quotaId) {
      setQuizzes([]);
      setPagination({
        total: 0,
        page: 1,
        limit: 10,
        totalPages: 0,
      });
      return;
    }

    const getQuizzes = async () => {
      try {
        setLoading(true);
        setError(null);

        const result = await fetchQuizzesByQuotaId(
          quotaId,
          searchTerm,
          pagination.page,
          pagination.limit
        );

        if (result.success && Array.isArray(result.data)) {
          setQuizzes(result.data);
          setPagination(result.pagination || pagination);
        } else {
          setQuizzes([]);
          setError("Invalid quiz data received");
        }
      } catch (err) {
        console.error("Error loading quizzes:", err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    getQuizzes();
  }, [quotaId, searchTerm, pagination.page, pagination.limit, refreshFlag]);

  const handleSearch = (e) => {
    e.preventDefault();
    setPagination((prev) => ({ ...prev, page: 1 }));
  };

  const handlePageChange = (newPage) => {
    if (newPage < 1 || newPage > pagination.totalPages) return;
    setPagination((prev) => ({ ...prev, page: newPage }));
  };

  const handleEditClick = async (quizId) => {
    try {
      const response = await fetch(`${apiBase}/quize/${quizId}`);
      if (!response.ok) throw new Error("Failed to fetch quiz details");
      const data = await response.json();
      setSelectedQuizId(quizId);
      setSelectedQuizData(data);
      setIsEditModalOpen(true);
    } catch (error) {
      console.error("Failed to load quiz:", error);
    }
  };

  const handleQuestionsClick = (quizId) => 
  {
    setSelectedQuizId(quizId);
    setIsQuestionsModalOpen(true);
  };

  const handleAnalyticsClick = (quizId) => 
  {
    setSelectedQuizId(quizId);
    setIsLeaderBoardModelOpen(true);
  };

  const handleModalClose = () => 
  {
    setIsEditModalOpen(false);
    setSelectedQuizId(null);
    setSelectedQuizData(null);
  };

  const handleQuestionsModalClose = () => {
    setIsQuestionsModalOpen(false);
    setSelectedQuizId(null);
  };

  const refreshAfterUpdate = () => {
    handleModalClose();
    setPagination((prev) => ({ ...prev })); // refresh same page
  };

  return (
    <div className="w-full space-y-4 mt-6">
      <div className="flex flex-col lg:flex-row  items-start gap-6 mb-6 lg:mb-0 lg:gap-0 lg:items-center justify-between">
        <h2 className="text-lg font-medium text-gray-900">Available Quizzes</h2>

        <form onSubmit={handleSearch} className="relative">
          <input
            type="text"
            placeholder="Search quizzes..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-9 pr-3 py-2 w-64 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            disabled={!quotaId || loading}
          />
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
          <button
            type="submit"
            className="ml-2 px-3 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
            disabled={!quotaId || loading}
          >
            Search
          </button>
        </form>
      </div>

      {error && (
        <div className="flex items-center p-4 text-sm bg-red-50 border border-red-200 rounded-md text-red-800">
          <AlertCircle className="w-5 h-5 mr-2 flex-shrink-0" />
          <span>{error}</span>
        </div>
      )}

      {loading ? (
        <AvailableQuizessSkeliton />
      ) : quizzes.length > 0 ? (
        <>
          <div className="overflow-x-auto border border-gray-200 rounded-md">
            <div className="min-w-full inline-block align-middle">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap">Title</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap">Time Limit</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap">Attempts Allowed</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap">Status</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {quizzes.map((quiz) => (
                    <tr key={quiz.quiz_id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 text-sm font-medium text-gray-900 whitespace-nowrap">{quiz.title}</td>
                      <td className="px-6 py-4 text-sm text-gray-500 whitespace-nowrap">{quiz.time_limit} minutes</td>
                      <td className="px-6 py-4 text-sm text-gray-500 whitespace-nowrap">{quiz.allowed_attempt_count}</td>
                      <td className="px-6 py-4 text-sm whitespace-nowrap">
                        <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full 
                          ${quiz.status === 1 ? "bg-green-100 text-green-800" : "bg-gray-100 text-gray-800"}`}>
                          {quiz.status === 1 ? "Active" : "Inactive"}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-500 whitespace-nowrap">
                        <div className="flex space-x-2">
                          <button
                            onClick={() => handleEditClick(quiz.quiz_id)}
                            className="inline-flex items-center px-3 py-1 text-xs font-medium bg-yellow-100 text-yellow-800 hover:bg-yellow-200 rounded-md"
                          >
                            <Settings className="w-4 h-4 mr-1" />
                            Settings
                          </button>
                          <button
                            onClick={() => handleQuestionsClick(quiz.quiz_id)}
                            className="inline-flex items-center px-3 py-1 text-xs font-medium bg-blue-100 text-blue-800 hover:bg-blue-200 rounded-md"
                          >
                            <Edit className="w-4 h-4 mr-1" />
                            Edit Questions
                          </button>
                          <button
                            onClick={() => handleAnalyticsClick(quiz.quiz_id)}
                            className="inline-flex items-center px-3 py-1 text-xs font-medium bg-blue-100 text-blue-800 hover:bg-blue-200 rounded-md"
                          >
                            <ChartNoAxesColumnDecreasing className="w-4 h-4 mr-1" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {pagination.totalPages > 1 && (
            <div className="flex items-center justify-between py-3 border-t border-gray-200">
              <div className="flex-1 flex justify-between items-center">
                <button
                  onClick={() => handlePageChange(pagination.page - 1)}
                  disabled={pagination.page === 1}
                  className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                >
                  Previous
                </button>
                <span className="text-sm text-gray-700">
                  Page {pagination.page} of {pagination.totalPages} ({pagination.total} items)
                </span>
                <button
                  onClick={() => handlePageChange(pagination.page + 1)}
                  disabled={pagination.page === pagination.totalPages}
                  className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </>
      ) : (
        <div className="py-10 text-center text-gray-500">
          {quotaId
            ? "No quizzes found for this quota. Try a different search term or quota."
            : "Please select a quota to view available quizzes."}
        </div>
      )}

      {isEditModalOpen && selectedQuizData && (
        <UpdateQuize
          isOpen={isEditModalOpen}
          onClose={handleModalClose}
          quizId={selectedQuizId}
          quizData={selectedQuizData}
          onQuizUpdated={refreshAfterUpdate}
          setRefreshFlag = {setRefreshFlag}
        />
      )}

      {isQuestionsModalOpen && selectedQuizId && (
        <QuizeQuestions 
          isOpen={isQuestionsModalOpen}
          onClose={handleQuestionsModalClose}
          quizId={selectedQuizId}
        />
      )}

      {
        isLeaderBoardModelOpen && selectedQuizId && (
          <LeaderBoard
            isOpen={isLeaderBoardModelOpen}
            quizId={selectedQuizId}
            onClose={() => setIsLeaderBoardModelOpen(false)}
          />
        )
      }
    </div>
  );
};

export default QuizList;